<template>
  <div
    class="countdown"
    :class="countdownCssClasses"
  >
    <div
      class="countdown__block"
      :style="customStyle"
    >
      <div class="countdown__cWrap">
        <span class="countdown__count">{{ days }}</span>
        <span>days</span>
      </div>
    </div>
    <div
      class="countdown__block"
      :style="customStyle"
    >
      <div class="countdown__cWrap">
        <span class="countdown__count">{{ hours }}</span>
        <span>hours</span>
      </div>
    </div>
    <div
      class="countdown__block"
      :style="customStyle"
    >
      <div class="countdown__cWrap">
        <span class="countdown__count">{{ minutes }}</span>
        <span>minutes</span>
      </div>
    </div>
    <div
      class="countdown__block"
      :style="customStyle"
    >
      <div class="countdown__cWrap">
        <span class="countdown__count">{{ seconds }}</span>
        <span>seconds</span>
      </div>
    </div>
    <div
      v-if="finished"
      class="countdown__overlay"
    >
      {{ finishedText }}
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import {
  parseISO,
  differenceInSeconds,
  intervalToDuration, eachDayOfInterval, differenceInCalendarDays
} from "date-fns";

@Component
export default class Countdown extends Vue {
  @Prop({default: ''}) cssClasses!: string;
  @Prop({default: ''}) backgroundColor!: string;
  @Prop() datetime!: string;
  @Prop({default: ''}) offset!: string;
  @Prop({default: 'Finished'}) finishedText!: string;
  @Prop({default: ''}) theme!: string;

  dateObj: Date = new Date();
  now: Date = new Date();
  interval = 0;

  data(): any {
    return {
      dateObj: parseISO(this.datetime),
      now: new Date(),
    };
  }

  created(): void {
    if (!this.finished) {
      this.interval = window.setInterval(() => {
        this.now = new Date();
      }, 1000);
    }
  }

  beforeDestroy(): void {
    if (this.interval > 0) {
      clearInterval(this.interval);
      this.interval = 0;
    }
  }

  get countdownCssClasses(): string {
    const cls: string[] = [];

    if (this.finished) {
      cls.push('countdown--finished');
    }

    if (this.theme) {
      cls.push(`countdown--${this.theme}`);
    }

    return cls.join(' ') + ' ' + this.cssClasses;
  }

  get countdownInSeconds(): number {
    return differenceInSeconds(this.dateObj, this.now);
  }

  get duration(): Duration {
    return intervalToDuration({start: this.finished ? this.now : this.dateObj, end: this.now})
  }

  get days(): number {
    return differenceInCalendarDays(this.finished ? this.now : this.dateObj, this.now);
  }

  get hours(): number {
    return this.duration.hours ?? 0;
  }

  get minutes(): number {
    return this.duration.minutes ?? 0;
  }

  get seconds(): number {
    return this.duration.seconds ?? 0;
  }

  get finished(): boolean {
    return this.countdownInSeconds < 1;
  }

  get customStyle(): Record<string, string> {
    const styles: Record<string, string> = {};
    if (this.backgroundColor) {
      styles["backgroundColor"] = this.backgroundColor;
    }

    return styles;
  }

  @Watch('finished', {immediate: true})
  onFinish(nV: boolean): void {
    if (nV) {
      clearInterval(this.interval);
    }
  }
}
</script>

<style scoped>
.countdown {
  display: flex;
  width: 100%;
  position: relative;
  justify-content: space-between;
}
.countdown--circles .countdown__block {
  border-radius: 50%;
}
.countdown__overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}
.countdown__block {
  border-radius: 2px;
  background: #242640;
  color: #fff;
  flex: none;
  padding-top: 20%;
  width: 20%;
  position: relative;
}
.countdown__block .countdown__cWrap {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
}
.countdown__count {
  font-weight: bold;
}
.countdown--finished .countdown__block {
  opacity: 0.2;
}
.countdown--important .countdown__block {
  background-color: #af5825;
}
.countdown--urgent .countdown__block {
  background-color: #8c0707;
}

</style>
